import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Modal, Form, Input, Select, DatePicker, message, Space, Alert } from 'antd'
import { PlusOutlined, LinkOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { LeanCustomsService } from '../services/leancustomsService'
import type { Company } from '../types/company'

const { TextArea } = Input

interface CreateLeanCustomsContractProps {
  company: Company
  onSuccess?: () => void
}

export const CreateLeanCustomsContract: React.FC<CreateLeanCustomsContractProps> = ({
  company,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const [modalVisible, setModalVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const [contractUrl, setContractUrl] = useState<string | null>(null)
  const [form] = Form.useForm()

  // Pre-fill form with company data
  React.useEffect(() => {
    if (company && modalVisible) {
      const today = dayjs()
      const street = company.street && company.house_number
        ? `${company.street} ${company.house_number}`
        : company.street || ''

      form.setFieldsValue({
        name: company.name || '',
        street: street,
        postcode: company.postal_code || null,
        place: company.city || '',
        country_code: company.country || 'NL',
        contract_type: 'direct',
        duration_type: 'indefinite',
        start_date: today,
        end_date: null,
        duration_years: 1,
        extension_years: 0,
        cancellation_months: 1,
        contract_reference: `CONTRACT-${company.id ? `COMPANY-${company.id}-` : ''}${company.name?.toUpperCase().replace(/\s+/g, '-') || 'UNNAMED'}-${Date.now()}`,
        description: '',
        locale: 'en',
        contract_language: 'en',
        version: '1.0',
        organization_type: 'company',
        coc_number: company.coc_number ? String(company.coc_number) : null,
        eori_number: company.eori_number || null,
        vat_number: company.vat_number || null,
      })
    }
  }, [company, modalVisible, form])

  const handleCreate = async () => {
    try {
      await form.validateFields()
      setLoading(true)

      const values = form.getFieldsValue()

      // Convert date fields from Dayjs to string format
      const contractData = {
        ...values,
        start_date: values.start_date
          ? (typeof values.start_date === 'string'
              ? values.start_date
              : dayjs(values.start_date as Dayjs).format('YYYY-MM-DD'))
          : dayjs().format('YYYY-MM-DD'),
        end_date: values.end_date
          ? (typeof values.end_date === 'string'
              ? values.end_date
              : dayjs(values.end_date as Dayjs).format('YYYY-MM-DD'))
          : values.duration_type === 'indefinite' ? null : undefined,
      }

      const result = await LeanCustomsService.createContract(contractData, company.id!)

      if (result.success && result.data) {
        if (result.data.signup_url) {
          setContractUrl(result.data.signup_url)
          message.success(t('customs.contract.created') || 'Contract created successfully!')
        } else {
          message.success(t('customs.contract.created') || 'Contract created successfully!')
          setModalVisible(false)
          if (onSuccess) {
            onSuccess()
          }
        }
      } else {
        message.error(result.error || t('customs.contract.createError') || 'Failed to create contract')
      }
    } catch (error) {
      console.error('Contract creation error:', error)
      message.error(
        error instanceof Error
          ? error.message
          : t('customs.contract.createError') || 'Failed to create contract'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleOpenLink = () => {
    if (contractUrl) {
      window.open(contractUrl, '_blank')
    }
  }

  const handleClose = () => {
    setModalVisible(false)
    setContractUrl(null)
    form.resetFields()
  }

  return (
    <>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        size="large"
        onClick={() => setModalVisible(true)}
      >
        {t('customs.createContract') || 'Create LeanCustoms Contract'}
      </Button>

      <Modal
        title={t('customs.createContract') || 'Create LeanCustoms Contract'}
        open={modalVisible}
        onCancel={handleClose}
        footer={null}
        width={600}
        destroyOnClose
      >
        {contractUrl ? (
          <div>
            <Alert
              message={t('customs.contract.created') || 'Contract created successfully!'}
              description={t('customs.contract.signupDescription') || 'Click the button below to open the signup form where you can complete the registration, upload documents, and verify your identity.'}
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Button
              type="primary"
              icon={<LinkOutlined />}
              size="large"
              block
              onClick={handleOpenLink}
            >
              {t('customs.contract.openSignupForm') || 'Open Signup Form'}
            </Button>
            <Button
              style={{ marginTop: 8 }}
              block
              onClick={handleClose}
            >
              {t('common.close') || 'Close'}
            </Button>
          </div>
        ) : (
          <Form form={form} layout="vertical" onFinish={handleCreate}>
            <Form.Item
              name="name"
              label={t('customs.contract.name') || 'Company Name'}
              rules={[{ required: true, message: t('customs.contract.nameRequired') || 'Name is required' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="street"
              label={t('customs.contract.street') || 'Street Address'}
              rules={[{ required: true, message: t('customs.contract.streetRequired') || 'Street address is required' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="postcode"
              label={t('customs.contract.postcode') || 'Postal Code'}
              rules={[{ required: true, message: t('customs.contract.postcodeRequired') || 'Postal code is required' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="place"
              label={t('customs.contract.place') || 'City'}
              rules={[{ required: true, message: t('customs.contract.placeRequired') || 'City is required' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="country_code"
              label={t('customs.contract.countryCode') || 'Country Code'}
              rules={[{ required: true, message: t('customs.contract.countryCodeRequired') || 'Country code is required' }]}
            >
              <Input maxLength={2} placeholder="NL" />
            </Form.Item>

            <Form.Item
              name="contract_type"
              label={t('customs.contract.type') || 'Contract Type'}
              rules={[{ required: true }]}
            >
              <Select>
                <Select.Option value="direct">Direct</Select.Option>
                <Select.Option value="fiscal">Fiscal</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="duration_type"
              label={t('customs.contract.durationType') || 'Duration Type'}
              rules={[{ required: true }]}
            >
              <Select>
                <Select.Option value="indefinite">Indefinite</Select.Option>
                <Select.Option value="fixed">Fixed</Select.Option>
                <Select.Option value="transaction">Transaction</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="start_date"
              label={t('customs.contract.startDate') || 'Start Date'}
              rules={[{ required: true }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.duration_type !== currentValues.duration_type
              }
            >
              {({ getFieldValue }) =>
                getFieldValue('duration_type') === 'fixed' ? (
                  <Form.Item
                    name="end_date"
                    label={t('customs.contract.endDate') || 'End Date'}
                    rules={[{ required: true }]}
                  >
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                ) : null
              }
            </Form.Item>

            <Form.Item
              name="contract_language"
              label={t('customs.contract.language') || 'Contract Language'}
              rules={[{ required: true }]}
            >
              <Select>
                <Select.Option value="en">English</Select.Option>
                <Select.Option value="nl">Dutch</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="contract_reference"
              label={t('customs.contract.reference') || 'Contract Reference'}
              rules={[{ required: true }]}
            >
              <Input readOnly disabled />
            </Form.Item>

            <Form.Item
              name="vat_number"
              label={t('customs.contract.vatNumber') || 'VAT Number'}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="eori_number"
              label={t('customs.contract.eoriNumber') || 'EORI Number'}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="coc_number"
              label={t('customs.contract.cocNumber') || 'Chamber of Commerce Number'}
            >
              <Input />
            </Form.Item>

            <Space style={{ width: '100%', justifyContent: 'flex-end', marginTop: 16 }}>
              <Button onClick={handleClose} disabled={loading}>
                {t('common.cancel') || 'Cancel'}
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {t('customs.contract.create') || 'Create Contract'}
              </Button>
            </Space>
          </Form>
        )}
      </Modal>
    </>
  )
}

